import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from '@/App.vue'
import '@/registerServiceWorker'
import '@/index.css'
import { createWebHistory, createRouter } from 'vue-router'
import { VueFire, VueFireAuth } from 'vuefire'
import { getFirebaseApp } from './firebase'
import { getAuth } from 'firebase/auth'
import { App as Capacitor } from '@capacitor/app'
import { useUserStore } from '@/stores/user'
import Cookie from '@/helpers/cookies'

// Vuetify
import '@/index.scss'
import '@mdi/font/css/materialdesignicons.css'
import { createVuetify } from 'vuetify'
import { VTimePicker } from 'vuetify/labs/VTimePicker'
import { VDialog, VCard, VSlider, VBtn } from 'vuetify/components'
import { aliases, mdi } from 'vuetify/iconsets/mdi'

const icons = {
  defaultSet: 'mdi',
  aliases,
  sets: {
    mdi,
  }
}
const vuetify = createVuetify({
  theme: {
    defaultTheme: 'light',
  },
  icons,
  components: {
    VTimePicker,
    VDialog,
    VCard,
    VSlider,
    VBtn,
  },
})

// Logged out.
import LandingPageView from '@/pages/LandingPage'
import LoginPageView from '@/pages/LoginPage'
import ForbiddenView from '@/pages/ForbiddenPage'

// Logged in.
import HomePageView from '@/pages/HomePage'
import ProfilePageView from '@/pages/ProfilePage'
import ToolkitPageView from '@/pages/ToolkitPage'

// Onboarding.
import OnboardingNameView from '@/pages/OnboardingName'
import OnboardingGoalView from '@/pages/OnboardingGoal'
import OnboardingPurposeView from '@/pages/OnboardingPurpose'
import OnboardingImportView from '@/pages/OnboardingImport'
import OnboardingQuestionsView from '@/pages/OnboardingQuestions'
import OnboardingTargetsView from '@/pages/OnboardingTargets'
import OnboardingAnalysisView from '@/pages/OnboardingAnalysis'
import OnboardingInsightsView from '@/pages/OnboardingInsights'
import OnboardingPlanView from '@/pages/OnboardingPlan'

// Data.
import DataConsistencyView from '@/pages/DataConsistency'
import DataQualityView from '@/pages/DataQuality'
import DataDurationView from '@/pages/DataDuration'

// Plan.
import PlanTargetView from '@/pages/PlanTargets'
import PlanStartView from '@/pages/PlanStart'
import PlanOutlineView from '@/pages/PlanOutline'
import PlanStreakView from '@/pages/PlanStreak'

// Admin.
import AdminPageView from '@/pages/AdminPage'
import AdminUsersPageView from '@/pages/AdminUsersPage'
import AdminUserPageView from '@/pages/AdminUserPage'

const routes = [
  { path: '/', component: HomePageView, name: 'Home', meta: { requiresAccount: true } },
  { path: '/profile', component: ProfilePageView, name: 'Profile', meta: { requiresAccount: true } },
  { path: '/login', component: LoginPageView },
  { path: '/data', redirect: { name: 'Duration' }},
  { path: '/onboarding/landing', component: LandingPageView , name: 'Landing' },
  { path: '/onboarding/name', component: OnboardingNameView, name: 'Onboarding Name' },
  { path: '/onboarding/goal', component: OnboardingGoalView, name: 'Onboarding Goal', meta: { requiresAccount: true } },
  { path: '/onboarding/purpose', component: OnboardingPurposeView, name: 'Onboarding Purpose', meta: { requiresAccount: true } },
  { path: '/onboarding/import', component: OnboardingImportView, name: 'Onboarding Import', meta: { requiresAccount: true } },
  { path: '/onboarding/questions', component: OnboardingQuestionsView, meta: { requiresAccount: true } },
  { path: '/onboarding/targets', component: OnboardingTargetsView, name: 'Onboarding Targets', meta: { requiresAccount: true } },
  { path: '/onboarding/analysis', component: OnboardingAnalysisView, name: 'Onboarding Analysis', meta: { requiresAccount: true } },
  { path: '/onboarding/insights', component: OnboardingInsightsView, name: 'Onboarding Insights', meta: { requiresAccount: true } },
  { path: '/onboarding/plan', component: OnboardingPlanView, name: 'Onboarding Plan', meta: { requiresAccount: true } },
  { path: '/data/consistency', component: DataConsistencyView, name: 'Consistency', meta: { requiresAccount: true } },
  { path: '/data/duration', component: DataDurationView, name: 'Duration', meta: { requiresAccount: true } },
  { path: '/data/quality', component: DataQualityView, name: 'Quality', meta: { requiresAccount: true } },
  { path: '/plan', redirect: { path: '/plan/targets' }},
  { path: '/plan/targets', component: PlanTargetView, meta: { requiresAccount: true } },
  { path: '/plan/start', component: PlanStartView, meta: { requiresAccount: true } },
  { path: '/plan/outline', component: PlanOutlineView, name: 'Mission Plan', meta: { requiresAccount: true } },
  { path: '/plan/streak', component: PlanStreakView, meta: { requiresAccount: true } },
  { path: '/toolkit', component: ToolkitPageView, meta: { requiresAccount: true } },
  { path: '/admin', component: AdminPageView },
  { path: '/admin/users', component: AdminUsersPageView },
  { path: '/admin/user', component: AdminUserPageView },
  { path: '/forbidden', component: ForbiddenView, name: 'Forbidden' },
  // TODO: { path: '/:pathMatch(.*)*', component: PathNotFound },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to) => {
  // Assumes that the user store has already been initialised.
  const userStore = useUserStore()
  const user = userStore.user

  // Handle admin pages.
  if (to.path.startsWith('/admin')) {
    if (Cookie.get('admin') !== process.env.VUE_APP_ADMIN_SECRET) {
      return { name: 'Home' }
    }
  }

  // If the page requires an account but the user is not logged in then we
  // redirect to onboarding. But if the user is logged in and has not completed
  // onboarding, then redirect them to the first onboarding page.
  if (to.meta.requiresAccount && !user) {
    if (to.path === '/') {
      return { name: 'Landing' }
    } else {
      return { name: 'Forbidden' }
    }
  }

  // Choose the correct onboarding page to show if onboarding is incomplete.
  if (to.meta.requiresAccount && !to.path.startsWith('/onboarding')) {
    const onboarded = userStore.completedOnboarding
    if (!onboarded) {
      if (!user || !user.name) {
        return { name: 'Onboarding Name' }
      } else if (!user.goal && user.goal !== 0) {
        return { name: 'Onboarding Goal' }
      } else if (!user.purpose) {
        return { name: 'Onboarding Purpose' }
      } else if (!user.hasDataSource) {
        return { name: 'Onboarding Import' }
      } else {
        return { name: 'Onboarding Targets' }
      }
    }
  }
})

const store = createPinia()

const { firebaseApp } = getFirebaseApp()

// Call `getAuth()` before the app is loaded so it is available everywhere else.
const auth = getAuth()
await auth.authStateReady()

// Initialise the user store with the current user if it exists.
const userStore = useUserStore(store)
await userStore.load()

let app = createApp(App)
  .use(router)
  .use(store)
  .use(VueFire, {
    firebaseApp,
    modules: [
      VueFireAuth()
    ]
  })
  .use(vuetify)
app.config.errorHandler = function (err, vm, info) {
  console.error(err, vm, info)
}
app.mount('#app')

Capacitor.addListener('appUrlOpen', function (event) {
  const slug = process.env.NODE_ENV === 'development' ? event.url.split(':8080').pop() : event.url.split('.me').pop()
  const query = slug.split('?').pop() || []
  let queryObject = {}
  let pairs = (query[0] === '?' ? query.substr(1) : query).split('&');
  for (let i = 0; i < pairs.length; i++) {
      let pair = pairs[i].split('=');
      queryObject[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }

  if (slug) {
    router.push({
      path: slug,
      query: queryObject,
    })
  }
})
